import React, { useCallback, useState } from 'react';
import { loadStripe, Stripe, StripeCheckoutDiscountAmount } from '@stripe/stripe-js';
import {
    CheckoutProvider,
    PaymentElement,
    AddressElement,
    useCheckout,
} from '@stripe/react-stripe-js';

import useStripeCheckoutSession from '../../hooks/useStripeCheckoutSession';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../AuthContext';
import { Account } from '../../core/_models';
import { managementGenerateStripeBillingPortalForAccount } from '../../core/_requests';
import useAccount from '../../hooks/useAccount';
import { useAxios } from '../../core/_axios';
import usePageTitle from '../../hooks/usePageTitle';
import { generateStripeBillingPortalForCurrentAccount } from '../../core/_requests_v2';
import { AccountFast } from '../../core/_models_fast';
import useAccountThatIsHydrated from '../../hooks/useAccountThatIsHydrated';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

/** ------------------------------------------------------------------
 *  HELPERS
 * ---------------------------------------------------------------- */

// Format currency with commas and no decimals
const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });
};

/** ------------------------------------------------------------------
 *  CONFIG
 * ---------------------------------------------------------------- */

const stripePromise = loadStripe(
    process.env.NODE_ENV === 'production'
        ? 'pk_live_51RSQJ5BAHiqbaXaYQJs4jfPiHf2Y9jJ9nEM07kP1qQNo4PCiBbIahxsuiW70J0bMwMLIRqO1VjbzJmICpWj0FseU00POwo7WES'
        : 'pk_test_51RSQJBBBSvUL7c7j3zzTQKw98f4gqi2QlPCN7nwqI33pP3MABqZpL4EoQhpaM6hmWObP6u2dejg1gBb7Gd5g4jAV00t6s5TnKu'
);

// Updated plan catalogue.
const PLANS = [
    {
        id: 'essentials_v1',
        name: 'Essentials',
        monthlyPrice: 149,
        yearlyPrice: 1490,
        description: 'For firms hosting up to 20 services per month.',
        features: [
            'Unlimited iPads & users',
            'Printable guestbook PDF',
            'CSV export',
            'Email support (24‑hr SLA)',
        ],
    },
    {
        id: 'growth_v1',
        name: 'Growth',
        monthlyPrice: 299,
        yearlyPrice: 2990,
        description: 'Up to 50 services per month, multiple locations.',
        features: [
            'Everything in Essentials',
            'Permanent online guestbook & store',
            'Attendance analytics dashboard',
            'Zapier & Bridge integrations',
            'Priority email & chat support',
        ],
    },
] as const;

/** ------------------------------------------------------------------
 *  PLAN CARD
 * ---------------------------------------------------------------- */

interface PlanCardProps {
    plan: (typeof PLANS)[number];
    billingPeriod: 'monthly' | 'yearly';
    active?: boolean; // user is currently on this plan
    selected?: boolean; // user is choosing this plan in picker
    onSelect?: (planId: string) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, billingPeriod, active, selected, onSelect }) => {
    const { copy } = useLocalizedCopy();
    const cardClass = [
        'max-w-md',
        'w-full',
        'mx-auto',
        'text-left',
        'rounded-2xl',
        'border',
        'p-6',
        'transition-all',
        selected ? 'border-primary ring-2 ring-primary' : '',
        active ? 'opacity-60 cursor-default' : 'hover:shadow-lg',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <button type="button" className={cardClass} onClick={() => onSelect?.(plan.id)} disabled={active}>
            <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">{plan.name}</h3>
                {active ? (
                    <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">{copy.Common.labels.active}</span>
                ) : selected ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">{copy.Common.labels.selected}</span>
                ) : null}
            </div>

            <p className="mt-2 text-gray-600">{plan.description}</p>
            <div className="mt-4">
                <p className="text-3xl font-bold">
                    {billingPeriod === 'yearly'
                        ? `$${formatCurrency(plan.yearlyPrice)}/year`
                        : `$${formatCurrency(plan.monthlyPrice)}/mo`}
                </p>
                {billingPeriod === 'yearly' && (
                    <p className="mt-1 text-sm text-green-600 font-medium">
                        Save ${formatCurrency(plan.monthlyPrice * 12 - plan.yearlyPrice)} (
                        {Math.round(((plan.monthlyPrice * 12 - plan.yearlyPrice) / (plan.monthlyPrice * 12)) * 100)}%)
                    </p>
                )}
            </div>

            <ul className="mt-4 space-y-1 text-sm text-gray-600">
                {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 shrink-0">
                            <path
                                fillRule="evenodd"
                                d="M2.25 12a9.75 9.75 0 1119.5 0 9.75 9.75 0 01-19.5 0zm14.53-2.28a.75.75 0 10-1.06-1.06L10.5 13.94l-1.72-1.72a.75.75 0 10-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l5.72-5.72z"
                                clipRule="evenodd"
                            />
                        </svg>
                        {f}
                    </li>
                ))}
            </ul>
        </button>
    );
};

/** ------------------------------------------------------------------
 *  PLAN PICKER
 * ---------------------------------------------------------------- */

interface PlanPickerProps {
    currentPlanId: string | null; // null if not subscribed yet
    onPlanChosen: (planId: string) => void;
    selectedPlanId: string | null;
    billingPeriod: 'monthly' | 'yearly';
    onBillingPeriodChange: (period: 'monthly' | 'yearly') => void;
}

const PlanPicker: React.FC<PlanPickerProps> = ({
    currentPlanId,
    onPlanChosen,
    selectedPlanId,
    billingPeriod,
    onBillingPeriodChange
}) => {
    // Strip _yearly suffix for comparison
    const getBasePlanId = (planId: string | null) => planId?.replace('_yearly', '') || null;
    const baseSelectedPlanId = getBasePlanId(selectedPlanId);
    const baseCurrentPlanId = getBasePlanId(currentPlanId);

    return (
        <div className="space-y-6">
            {/* Billing Period Toggle */}
            <div className="flex justify-center">
                <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
                    <button
                        type="button"
                        onClick={() => onBillingPeriodChange('monthly')}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                            billingPeriod === 'monthly'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Monthly
                    </button>
                    <button
                        type="button"
                        onClick={() => onBillingPeriodChange('yearly')}
                        className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                            billingPeriod === 'yearly'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        Yearly
                        <span className="ml-1.5 text-xs text-green-600 font-semibold">Save 17%</span>
                    </button>
                </div>
            </div>

            {/* Plan Cards */}
            <div className="grid gap-6 sm:grid-cols-2">
                {PLANS.map((plan) => (
                    <PlanCard
                        key={plan.id}
                        plan={plan}
                        billingPeriod={billingPeriod}
                        active={plan.id === baseCurrentPlanId}
                        selected={plan.id === baseSelectedPlanId && baseCurrentPlanId !== plan.id}
                        onSelect={onPlanChosen}
                    />
                ))}
            </div>
        </div>
    );
};

/** ------------------------------------------------------------------
 *  BILLING SUMMARY (placeholder)
 * ---------------------------------------------------------------- */

const BillingSummary: React.FC<{
    account: Account;
    isForCurrentAccount?: boolean;
    onChangePlan: () => void
}> = ({
    account,
    isForCurrentAccount = false,
    onChangePlan
}) => {
        const axios = useAxios();
        const { copy } = useLocalizedCopy();

        const plan = PLANS.find((p) => p.id === account.billing_data.our_plan_id)

        const [generatingPortal, setGeneratingPortal] = useState<boolean>(false);
        const [tryingToGeneratePortal, setTryingToGeneratePortal] = useState<'cancel-subscription' | 'change-payment-method' | 'change-plan' | 'manage-subscription' | null>(null);
        const [errorGeneratingPortal, setErrorGeneratingPortal] = useState<string | null>(null);

        const onGenerateStripeBillingPortal = useCallback(async (typeOfPortal: 'cancel-subscription' | 'change-payment-method' | 'change-plan' | 'manage-subscription') => {
            setGeneratingPortal(true);
            setTryingToGeneratePortal(typeOfPortal);
            setErrorGeneratingPortal(null);
            try {
                if (isForCurrentAccount) {
                    const redirectToUrl = await generateStripeBillingPortalForCurrentAccount(axios, {
                        typeOfPortal,
                    });
                    window.location.href = redirectToUrl;
                } else {
                    const redirectToUrl = await managementGenerateStripeBillingPortalForAccount(axios, account.unique_identifier, {
                        typeOfPortal,
                    });
                    window.location.href = redirectToUrl;
                }
            } catch (error) {
                console.error('Error generating Stripe billing portal:', error);
                setErrorGeneratingPortal(copy.Errors.generic + '. ' + copy.Common.messages.tryAgain);
                setGeneratingPortal(false);
            }
        }, [axios, account.unique_identifier, isForCurrentAccount]);

        if (generatingPortal) {
            return (
                <div className="flex justify-center py-12">
                    <LoadingSpinner />
                </div>
            );
        }

        if (errorGeneratingPortal) {
            return (
                <div className="rounded border border-red-400 bg-red-100 p-4 text-red-700">
                    <p>{errorGeneratingPortal}</p>
                    {tryingToGeneratePortal && <button className="btn btn-light mt-4" onClick={() => onGenerateStripeBillingPortal(tryingToGeneratePortal)}>
                        {copy.Common.messages.tryAgain}
                    </button>}
                </div>
            );
        }

        if (!plan) {
            return (
                <div className="rounded-2xl border p-8">
                    <h2 className="text-xl font-semibold">{copy.Account.billing.currentPlan}</h2>
                    <p className="mt-1 text-gray-600">{copy.Common.labels.select} {copy.Account.billing.currentPlan} {copy.Common.buttons.continue}</p>
                    <button type="button" className="btn btn-light mt-4" onClick={onChangePlan}>
                        {copy.Account.billing.changePlan}
                    </button>
                </div>
            );
        }

        return (
            <div className="space-y-6 rounded-2xl border p-8">
                {account.billing_data.stripe_subscription_cancel_at && <div>
                    <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
                        <span className="block sm:inline">
                            {copy.Account.billing.cancelSubscription} {new Date(account.billing_data.stripe_subscription_cancel_at * 1000).toLocaleDateString()}.
                        </span>
                    </div>
                    <button
                        type="button"
                        className="btn btn-light mt-4"
                        onClick={() => onGenerateStripeBillingPortal('manage-subscription')}
                    >
                        {copy.Account.billing.cancelSubscription}
                    </button>
                </div>}
                <div>
                    <h2 className="text-xl font-semibold">{copy.Account.billing.currentPlan}</h2>
                    <p className="mt-1 text-gray-600">
                        <span className="font-medium">{plan.name}</span> – {
                            account.billing_data.our_plan_id?.includes('_yearly')
                                ? `$${formatCurrency(plan.yearlyPrice)}/year`
                                : `$${formatCurrency(plan.monthlyPrice)}/mo`
                        }
                    </p>
                    <div className="flex gap-2 mt-4">
                        <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={() => onGenerateStripeBillingPortal('change-plan')}
                        >
                            {copy.Account.billing.changePlan}
                        </button>
                        <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={() => onGenerateStripeBillingPortal('manage-subscription')}
                        >
                            {copy.Account.billing.title}
                        </button>
                    </div>
                    <div className="mt-4">
                        <div className="mt-2 flex items-center gap-2">
                            <span className="font-medium">{copy.Account.billing.paymentMethod}:</span>

                            {(() => {
                                const {
                                    stripe_customer_default_payment_method_details: customerPM,
                                    stripe_subscription_default_payment_method_card_details: subCardPM,
                                } = account.billing_data;

                                /* ---------- prefer customer-level PM ---------- */
                                if (customerPM) {
                                    switch (customerPM.type) {
                                        case "card":
                                            return (
                                                <span>
                                                    {customerPM.brand.charAt(0).toUpperCase() + customerPM.brand.slice(1)}
                                                    {" "}**** {customerPM.last4}
                                                    {" "} (exp {customerPM.exp_month}/{customerPM.exp_year})
                                                    {customerPM.wallet && <> — {customerPM.wallet.replace(/_/g, " ")}</>}
                                                </span>
                                            );

                                        case "us_bank_account":
                                            return (
                                                <span>
                                                    {customerPM.bank_name} ****{customerPM.last4} ({customerPM.account_type})
                                                </span>
                                            );

                                        case "sepa_debit":
                                            return (
                                                <span>
                                                    SEPA ****{customerPM.last4} (bank code {customerPM.bank_code})
                                                </span>
                                            );

                                        case "cashapp":
                                            return <span>Cash App — {customerPM.cashtag}</span>;

                                        case "amazon_pay":
                                            return <span>Amazon Pay</span>;

                                        /* literal "unknown" catches anything your handler hasn’t modeled yet */
                                        case "unknown":
                                        default:
                                            return (
                                                <span className="italic text-gray-400">
                                                    {customerPM.note ?? "Unknown payment method"}
                                                </span>
                                            );
                                    }
                                }

                                /* ---------- fallback to subscription card ---------- */
                                if (subCardPM) {
                                    return (
                                        <span>
                                            {subCardPM.brand.charAt(0).toUpperCase() + subCardPM.brand.slice(1)}
                                            {" "}**** {subCardPM.last4}
                                            {" "} (exp {subCardPM.exp_month}/{subCardPM.exp_year})
                                        </span>
                                    );
                                }

                                /* ---------- nothing available ---------- */
                                return <span className="italic text-gray-400">N/A</span>;
                            })()}
                        </div>

                        <div>
                            <button
                                type="button"
                                className="btn btn-light btn-sm mt-2"
                                onClick={() => onGenerateStripeBillingPortal('change-payment-method')}
                            >
                                {copy.Account.billing.updatePayment}
                            </button>
                        </div>
                    </div>

                </div>

                <div className="text-gray-600 text-sm space-y-1">

                    <div>
                        <span className="font-medium">Stripe Subscription ID:</span>{' '}
                        {account.billing_data.stripe_subscription_id || <span className="italic text-gray-400">N/A</span>}
                    </div>
                    <div>
                        <span className="font-medium">Stripe Customer ID:</span>{' '}
                        {account.billing_data.stripe_customer_id || <span className="italic text-gray-400">N/A</span>}
                    </div>

                </div>

                {account.billing_data.stripe_latest_paid_invoice && (
                    <div>
                        <h3 className="font-medium mb-1">{copy.Account.billing.invoices}</h3>
                        <div className="text-gray-600 text-sm space-y-1">
                            <div>
                                <span className="font-medium">{copy.Account.billing.invoices} #:</span> {account.billing_data.stripe_latest_paid_invoice.number}
                            </div>
                            <div>
                                <span className="font-medium">Period:</span>{' '}
                                {new Date(account.billing_data.stripe_latest_paid_invoice.period_start * 1000).toLocaleDateString()} -{' '}
                                {new Date(account.billing_data.stripe_latest_paid_invoice.period_end * 1000).toLocaleDateString()}
                            </div>
                            <div>
                                <span className="font-medium">Total:</span>{' '}
                                ${(account.billing_data.stripe_latest_paid_invoice.total / 100).toFixed(2)}
                            </div>
                            <div>
                                <span className="font-medium">Issued:</span>{' '}
                                {new Date(account.billing_data.stripe_latest_paid_invoice.created * 1000).toLocaleDateString()}
                            </div>
                            <div>
                                <span className="font-medium">Status:</span>{' '}
                                {account.billing_data.stripe_latest_paid_invoice.status_transitions.paid_at
                                    ? 'Paid'
                                    : 'Unpaid'}
                            </div>
                            <div className="flex gap-4 mt-2">
                                <a
                                    href={account.billing_data.stripe_latest_paid_invoice.hosted_invoice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-light btn-sm"
                                >
                                    {copy.Common.buttons.view} {copy.Account.billing.invoices}
                                </a>
                                <a
                                    href={account.billing_data.stripe_latest_paid_invoice.invoice_pdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-light btn-sm"
                                >
                                    {copy.Account.billing.download} PDF
                                </a>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-4">
                    <h3 className="font-medium mb-1">{copy.Account.billing.title}</h3>
                    <div>
                        <button
                            type="button"
                            className="btn btn-danger btn-sm mt-2"
                            onClick={() => onGenerateStripeBillingPortal('cancel-subscription')}
                        >
                            {copy.Account.billing.cancelSubscription}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

/** ------------------------------------------------------------------
 *  CHECKOUT FORM
 * ---------------------------------------------------------------- */

const CheckoutForm: React.FC<{ onSuccess: () => void; isNewSubscription: boolean }> = ({ onSuccess, isNewSubscription }) => {
    const { currentUser } = useAuth();
    const checkout = useCheckout();
    const { copy } = useLocalizedCopy();

    const [promo, setPromo] = useState('');
    const [promoError, setPromoError] = useState<string | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [appliedDiscounts, setAppliedDiscounts] = useState<Array<StripeCheckoutDiscountAmount> | null>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const result = await checkout.confirm({
                email: currentUser.email
            });
            if (result.type === 'error') {
                setError(result.error.message);
                setSubmitting(false);
            } else {
                onSuccess();
            }
        } catch (err) {
            console.error(err);
            setError(copy.Errors.generic + '. ' + copy.Common.messages.tryAgain);
            setSubmitting(false);
        }
    };

    const applyCode = async () => {
        const result = await checkout.applyPromotionCode(promo);
        console.log('applyCode result:', result);
        if (result.type === 'success') {
            setPromoError(null);
            setAppliedDiscounts(result.session.discountAmounts || null);
            setPromo(''); // clear promo input
        } else {
            setPromoError(result.error.message || copy.Errors.generic);
            setAppliedDiscounts(null);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-6 px-4">
            <AddressElement
                options={{ mode: 'billing' }}
            />
            <PaymentElement />
            {/* Promo-code UI */}
            <div className="flex gap-2">
                <input
                    type="text"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    className="flex-grow input input-bordered"
                    placeholder={copy.Common.labels.optional}
                />
                <button
                    type="button"
                    onClick={applyCode}
                    className="btn btn-secondary"
                    disabled={!promo}
                >
                    {copy.Common.buttons.submit}
                </button>
            </div>
            {promoError && <p className="text-sm text-red-600">{promoError}</p>}
            {appliedDiscounts && appliedDiscounts.length > 0 && (
                <div className="bg-green-50 border border-green-300 rounded p-3 text-green-800 text-sm space-y-1">
                    <div className="font-medium">Applied Discount{appliedDiscounts.length > 1 ? 's' : ''}:</div>
                    <ul className="list-disc ml-5">
                        {appliedDiscounts.map((discount, idx) => (
                            <li key={discount.promotionCode || idx}>
                                {discount.displayName
                                    ? <span className="font-semibold">{discount.displayName}</span>
                                    : discount.promotionCode}
                                {discount.percentOff != null && (
                                    <> — {discount.percentOff}% off</>
                                )}
                                {discount.amount && (
                                    <> — {discount.amount}</>
                                )}
                                {discount.recurring && discount.recurring.type && (
                                    <> ({discount.recurring.type})</>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <button type="submit" disabled={submitting} className="btn btn-primary btn-lg w-full">
                {submitting ? (
                    <>
                        <LoadingSpinner /> {copy.Common.labels.processing}...
                    </>
                ) : (
                    isNewSubscription ? copy.Account.billing.subscribe : copy.Account.billing.confirmPlanChange
                )}
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
    );
};

/** ------------------------------------------------------------------
 *  MAIN COMPONENT
 * ---------------------------------------------------------------- */

function ActualAccountBillingForm({
    account,
    isForCurrentAccount = false,
}: {
    account?: Account;
    isForCurrentAccount?: boolean;
}) {
    const { copy } = useLocalizedCopy();
    const initiallyIsSubscribed = account.billing_data?.is_subscribed || false

    const initialPlanId = account.billing_data?.our_plan_id || null;

    const initiallySelectedPlanId = account.billing_data?.our_plan_id || 'essentials_v1'; // default to essentials if not set

    const [isSubscribed, setIsSubscribed] = useState<boolean>(
        initiallyIsSubscribed
    );
    const [currentPlanId, setCurrentPlanId] = useState<string | null>(
        initialPlanId
    );
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
        initiallySelectedPlanId
    );
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

    // Only generate a checkout session after a plan is chosen
    const {
        loading: loadingCheckoutSession,
        loadingError,
        checkoutSessionClientSecret,
        onRetry,
    } = useStripeCheckoutSession({
        isSubscribed,
        accountUniqueId: isForCurrentAccount ? undefined : account?.unique_identifier, // deliberately not using useAccount here
        selectedPlanId: selectedPlanId
    });

    const handlePlanChosen = (planId: string) => {
        // Append _yearly suffix if yearly billing is selected
        const finalPlanId = billingPeriod === 'yearly' ? `${planId}_yearly` : planId;
        setSelectedPlanId(finalPlanId);
    };

    const handleBillingPeriodChange = (period: 'monthly' | 'yearly') => {
        setBillingPeriod(period);
        // Reset selected plan when switching billing periods to force re-selection
        setSelectedPlanId(null);
    };

    const handleCheckoutSuccess = () => {
        setIsSubscribed(true);
        setCurrentPlanId(selectedPlanId!);
        setSelectedPlanId(null);
    };

    /**
     * RENDER
     * ------------------------------------------------------------------ */
    if (isSubscribed) {
        return (
            <BillingSummary
                account={account}
                isForCurrentAccount={isForCurrentAccount}
                onChangePlan={() => {
                    setIsSubscribed(false); // temporarily leave subscribed flow to re-select plan
                }}
            />
        );
    }

    return (
        <div className="space-y-4">
            <h1 className="text-xl font-semibold">
                {currentPlanId ? copy.Account.billing.changePlan : copy.Account.billing.choosePlan}
            </h1>

            <PlanPicker
                currentPlanId={currentPlanId}
                selectedPlanId={selectedPlanId}
                onPlanChosen={handlePlanChosen}
                billingPeriod={billingPeriod}
                onBillingPeriodChange={handleBillingPeriodChange}
            />

            {/* Show checkout only when a *different* plan is picked or user is new */}
            {!isSubscribed || (selectedPlanId && selectedPlanId !== currentPlanId) ? (
                <>
                    {loadingCheckoutSession && (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner />
                        </div>
                    )}
                    {loadingError && (
                        <div className="rounded border border-red-400 bg-red-100 p-4 text-red-700">
                            <p>{loadingError}</p>
                            <button className="btn btn-light mt-4" onClick={onRetry}>
                                {copy.Common.messages.tryAgain}
                            </button>
                        </div>
                    )}
                    {checkoutSessionClientSecret && (
                        <CheckoutProvider
                            stripe={stripePromise as Promise<Stripe | null>}
                            options={{
                                fetchClientSecret: () => Promise.resolve(checkoutSessionClientSecret),
                            }}
                        >
                            <CheckoutForm
                                onSuccess={handleCheckoutSuccess}
                                isNewSubscription={!currentPlanId}
                            />
                        </CheckoutProvider>
                    )}
                </>
            ) : null}
        </div>
    );
}


export default function AccountBillingForm({
    accountUniqueIdentifier,
    isForCurrentAccount = false,
}: {
    accountUniqueIdentifier?: string | null;
    isForCurrentAccount?: boolean;
}) {

    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    usePageTitle(copy.Account.billing.title);

    const {
        loading,
        loadingError,
        account,
        onLoadAccount: loadAccount,
    } = useAccountThatIsHydrated({
        axios,
        isForCurrentAccount,
        accountUniqueId: accountUniqueIdentifier || null,
    });

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadAccount}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <ActualAccountBillingForm
            account={account}
            isForCurrentAccount={isForCurrentAccount}
        />
    );
}